/**
 * useAnalytics Hook
 * Custom hook for analytics data.
 *
 * All three endpoints accept the same optional filters (AI agent / human
 * assignee / channel) alongside the date range — omitted filters reproduce
 * unfiltered behavior (backend contract, analytics.controller.ts).
 */

import { useState } from 'react';
import apiService from '@/services/api.service';

interface DateRange {
  startDate: Date;
  endDate: Date;
}

/** Optional analytics filter dimensions, threaded to every endpoint. */
interface AnalyticsFilters {
  /** AI agent's _id (NOT its name — matched at the message level against
   *  WhatsAppMessage.metadata.selectedAgent, since a single conversation can
   *  be handled by more than one agent over its life). */
  agentId?: string;
  /** Team member ObjectId (Conversation.assignedTo). */
  assignedTo?: string;
  channel?: 'whatsapp' | 'instagram' | '';
}

interface ChatMetrics {
  totalConversations: number;
  totalMessages: number;
  botHandledConversations: number;
  humanEscalatedConversations: number;
  resolvedConversations: number;
  activeConversations: number;
}

type TimeSeriesGranularity = 'hour' | 'day' | 'week';

interface TimeSeriesBucket {
  /** ISO timestamp of the bucket start. */
  bucket: string;
  messages: number;
  conversations: number;
}

interface TimeSeriesData {
  granularity: TimeSeriesGranularity;
  currentPeriod: TimeSeriesBucket[];
  previousPeriod: TimeSeriesBucket[];
}

interface CountryStats {
  countryCode: string;
  countryName: string;
  conversations: number;
  /** Server-computed share of totalConversations (0-100, 1 decimal). */
  percentagePct: number;
}

interface ChatsByCountry {
  countries: CountryStats[];
  totalConversations: number;
}

/** Per-agent message volume for a window — `agentId` is the Agent `_id`;
 *  the caller resolves it to a name from its own agent list (a deleted
 *  agent's id just won't resolve, instead of the row vanishing). */
interface AgentMessageBreakdown {
  agentId: string;
  messages: number;
  conversations: number;
}

interface MessagesByAgent {
  agents: AgentMessageBreakdown[];
  /** Messages with no bot-agent attribution (customer replies, human/manual
   *  sends, campaign sends) — surfaced honestly rather than silently dropped. */
  unattributedMessages: number;
}

/** Build the query params object shared by all three endpoints. */
function buildParams(startDate: Date, endDate: Date, filters?: AnalyticsFilters) {
  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    ...(filters?.agentId ? { agentId: filters.agentId } : {}),
    ...(filters?.assignedTo ? { assignedTo: filters.assignedTo } : {}),
    ...(filters?.channel ? { channel: filters.channel } : {}),
  };
}

export const useAnalytics = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getChatMetrics = async (
    startDate: Date,
    endDate: Date,
    filters?: AnalyticsFilters
  ): Promise<ChatMetrics | null> => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get<{ success: boolean; data: ChatMetrics }>(
        '/admin/analytics/chats/metrics',
        { params: buildParams(startDate, endDate, filters) }
      );
      return response.success ? response.data : null;
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to fetch chat metrics');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getChatStartedTimeSeries = async (
    startDate: Date,
    endDate: Date,
    filters?: AnalyticsFilters
  ): Promise<TimeSeriesData | null> => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get<{ success: boolean; data: TimeSeriesData }>(
        '/admin/analytics/chats/time-series',
        { params: buildParams(startDate, endDate, filters) }
      );
      return response.success ? response.data : null;
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to fetch time series data');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getChatsByCountry = async (
    startDate: Date,
    endDate: Date,
    filters?: AnalyticsFilters
  ): Promise<ChatsByCountry | null> => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get<{ success: boolean; data: ChatsByCountry }>(
        '/admin/analytics/chats/by-country',
        { params: buildParams(startDate, endDate, filters) }
      );
      if (!response.success) return null;
      return {
        countries: response.data?.countries || [],
        totalConversations: response.data?.totalConversations ?? 0,
      };
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to fetch country statistics');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getMessagesByAgent = async (
    startDate: Date,
    endDate: Date,
    filters?: AnalyticsFilters
  ): Promise<MessagesByAgent | null> => {
    try {
      setLoading(true);
      setError(null);
      // agentId is meaningless here (it names the dimension being broken
      // down BY), so it's deliberately stripped before building params.
      const { agentId: _agentId, ...conversationFilters } = filters || {};
      const response = await apiService.get<{ success: boolean; data: MessagesByAgent }>(
        '/admin/analytics/chats/by-agent',
        { params: buildParams(startDate, endDate, conversationFilters) }
      );
      if (!response.success) return null;
      return {
        agents: response.data?.agents || [],
        unattributedMessages: response.data?.unattributedMessages ?? 0,
      };
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to fetch per-agent breakdown');
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    getChatMetrics,
    getChatStartedTimeSeries,
    getChatsByCountry,
    getMessagesByAgent,
  };
};

export type {
  DateRange,
  AnalyticsFilters,
  ChatMetrics,
  TimeSeriesGranularity,
  TimeSeriesBucket,
  TimeSeriesData,
  CountryStats,
  ChatsByCountry,
  AgentMessageBreakdown,
  MessagesByAgent,
};
